/**
 * Which onboard-as mode a client is in for this customer (Entity Selection First,
 * VME-4142, slice 1). The server decides it at activation; afterwards the portal follows the
 * customer's permission requests. Pure, so every rule is unit-tested.
 *
 * - AskFirst: the entity selection flow (choose, confirm, then the wizards) for that client.
 * - Legacy: main's flow, unchanged.
 */
import { apiSchemas } from '@verifime/api-definition';
import type { TEntityChoice, TSelectableEntityType } from '@verifime/components';
import {
  readDeclaredEntityType,
  type TDeclaredEntityType,
  type TOnboardingWorkflowType,
} from '@verifime/views/src/EntitySelection/requestCriteria';

// The declaration-to-level mapping lives in the views (ADR 62); these are the app's names for it.
// Imported from the module, not the `@verifime/views` barrel: many page tests mock the barrel with
// a partial factory, which would leave these pure helpers undefined.
export {
  DECLARED_ENTITY_TYPES,
  effectiveDeclaredLevel,
  readDeclaredEntityType,
  type TDeclaredEntityLevel,
  type TDeclaredEntityType,
  type TOnboardingWorkflowType,
} from '@verifime/views/src/EntitySelection/requestCriteria';

export type TOnboardAsMode = 'AskFirst' | 'Legacy';

/** The activation's answer for one invite, kept in session storage beside the tracking reference. */
export type TInviteOnboardAsMode = {
  trackingReference: string;
  /** The invite's client; absent when it could not be read (then the answer names no client). */
  organisationId?: string;
  onboardAsMode: TOnboardAsMode;
  /**
   * The entity type the partner declared, kept only when the activation decided AskFirst. Absent
   * for no declaration, an unknown value, or a Legacy answer.
   */
  declaredEntityType?: TDeclaredEntityType;
  /**
   * When the activation answered (ISO 8601). An AskFirst answer holds only while no request for the
   * client was created or completed after it.
   */
  answeredAt?: string;
};

/** The fields of a `PermissionRequestSummary` (`GET /v1/permission/request/me`) read here. */
export type TOnboardAsModeRequest = {
  id?: string;
  organisationId?: string;
  organisationName?: string;
  /** Legacy, Individual or one of the seven entity types. Absent or unknown reads as Legacy. */
  requestType?: string;
  /** Absent while the request is open. */
  completedDateTime?: string | null;
  createdDateTime?: string;
  /** Set when the request was closed by a grant; a discarded request has none. */
  permissionId?: string | null;
  onboardingWorkflowType?: TOnboardingWorkflowType;
};

/**
 * The entity request types: every `PermissionRequestType` but Legacy and Individual, from the
 * generated API enum (as the views package reads them), so a type added to the contract is never
 * read as Legacy here.
 */
const ENTITY_REQUEST_TYPES = apiSchemas.PermissionRequestType.options.filter(
  (requestType) => requestType !== 'Legacy' && requestType !== 'Individual',
) as readonly string[] as readonly TSelectableEntityType[];

const isEntityType = (value: unknown): value is TSelectableEntityType =>
  ENTITY_REQUEST_TYPES.includes(value as TSelectableEntityType);

const isOpen = (request: TOnboardAsModeRequest) => !request.completedDateTime;

/**
 * The activation response's `onboardAsMode`. An absent one (a server from before this change, or a task)
 * is Legacy. Anything but `AskFirst` or `Legacy` is `unknown`: the caller shows a neutral error rather
 * than guessing.
 */
export function readActivationOnboardAsMode(onboardAsMode: unknown): TOnboardAsMode | 'unknown' {
  if (onboardAsMode === undefined || onboardAsMode === null) {
    return 'Legacy';
  }
  if (onboardAsMode === 'AskFirst' || onboardAsMode === 'Legacy') {
    return onboardAsMode;
  }
  return 'unknown';
}

/**
 * The declared type to keep with an activation answer. Legacy ignores it (verifime-adr#568 Q5):
 * a Legacy-decided activation behaves exactly as main.
 */
export function activationDeclaredEntityType(
  onboardAsMode: TOnboardAsMode,
  declaredEntityType: unknown,
): TDeclaredEntityType | undefined {
  return onboardAsMode === 'AskFirst' ? readDeclaredEntityType(declaredEntityType) : undefined;
}

/** The entity type of an entity request, or undefined for any other request. */
export function entityRequestType(
  request: TOnboardAsModeRequest,
): TSelectableEntityType | undefined {
  return isEntityType(request.requestType) ? request.requestType : undefined;
}

/** True for an Individual or entity request: one created by the entity selection flow. */
export function isAskFirstRequest(request: TOnboardAsModeRequest): boolean {
  return request.requestType === 'Individual' || entityRequestType(request) !== undefined;
}

/**
 * The requests main's flow handles: Legacy, plus any request with an absent or unknown type.
 * On data with no ask-first request it returns every request, in order.
 */
export function legacyRequests<T extends TOnboardAsModeRequest>(requests: T[]): T[] {
  return requests.filter((request) => !isAskFirstRequest(request));
}

/** This client's open Individual and entity requests. */
export function openAskFirstRequestsFor<T extends TOnboardAsModeRequest>(
  requests: T[] | undefined,
  organisationId: string | undefined,
): T[] {
  if (!organisationId) {
    return [];
  }
  return (requests ?? []).filter(
    (request) =>
      request.organisationId === organisationId && isOpen(request) && isAskFirstRequest(request),
  );
}

const timeOf = (value: string | null | undefined) => {
  const time = Date.parse(value ?? '');
  return Number.isNaN(time) ? undefined : time;
};

/**
 * True when the request was created or completed after the answer, or its dates cannot be read
 * (then it may be, so the answer is treated as stale).
 */
const isAfter = (request: TOnboardAsModeRequest, answeredAt: number) => {
  const created = timeOf(request.createdDateTime);
  const completed = request.completedDateTime ? timeOf(request.completedDateTime) : undefined;
  if (created === undefined || (request.completedDateTime && completed === undefined)) {
    return true;
  }
  return created > answeredAt || (completed !== undefined && completed > answeredAt);
};

/**
 * A client's onboard-as mode for this customer. AskFirst when the customer has an open Individual or entity
 * request for it. Also AskFirst when the current invite's activation decided AskFirst for it and that
 * answer still holds: the client has no open request, and no request for it was created or
 * completed after the answer (`answeredAt`; an answer without it holds only while the client has
 * no request at all). Legacy otherwise, including once the journey's requests close after the
 * answer.
 *
 * `inviteOnboardAsMode` must be the current invite's answer (the tracking reference in session storage),
 * and `requests` every request of the customer, open and closed.
 */
export function clientOnboardAsMode({
  organisationId,
  requests,
  inviteOnboardAsMode,
}: {
  organisationId: string | undefined;
  requests: TOnboardAsModeRequest[] | undefined;
  inviteOnboardAsMode: TInviteOnboardAsMode | undefined;
}): TOnboardAsMode {
  if (!organisationId) {
    return 'Legacy';
  }
  if (openAskFirstRequestsFor(requests, organisationId).length > 0) {
    return 'AskFirst';
  }
  if (
    inviteOnboardAsMode?.onboardAsMode !== 'AskFirst' ||
    inviteOnboardAsMode.organisationId !== organisationId
  ) {
    return 'Legacy';
  }
  const clientRequests = (requests ?? []).filter(
    (request) => request.organisationId === organisationId,
  );
  const answeredAt = timeOf(inviteOnboardAsMode.answeredAt);
  const answerHolds =
    answeredAt === undefined
      ? clientRequests.length === 0
      : clientRequests.every((request) => !isOpen(request) && !isAfter(request, answeredAt));
  return answerHolds ? 'AskFirst' : 'Legacy';
}

/** A client with an ask-first journey to resume, for Home. */
export type TJourneyClientEntry = {
  organisationId: string;
  organisationName: string | undefined;
  openRequestCount: number;
};

/**
 * Every client with open Individual or entity requests, once each in first-seen order, with its
 * name and how many such requests are open: one resume entry per client.
 */
export function journeyClients(
  requests: TOnboardAsModeRequest[] | undefined,
): TJourneyClientEntry[] {
  const byClient = new Map<string, TJourneyClientEntry>();
  for (const request of requests ?? []) {
    if (!request.organisationId || !isOpen(request) || !isAskFirstRequest(request)) {
      continue;
    }
    const entry = byClient.get(request.organisationId) ?? {
      organisationId: request.organisationId,
      organisationName: request.organisationName,
      openRequestCount: 0,
    };
    entry.openRequestCount += 1;
    byClient.set(request.organisationId, entry);
  }
  return [...byClient.values()];
}

/**
 * The client with the most recently created open Individual or entity request: where a customer
 * with no identities resumes.
 */
export function mostRecentJourneyClient(
  requests: TOnboardAsModeRequest[] | undefined,
): string | undefined {
  return (requests ?? [])
    .filter((request) => request.organisationId && isOpen(request) && isAskFirstRequest(request))
    .sort(
      (a, b) =>
        (timeOf(b.createdDateTime) ?? Number.NEGATIVE_INFINITY) -
        (timeOf(a.createdDateTime) ?? Number.NEGATIVE_INFINITY),
    )[0]?.organisationId;
}

/** The entity types of these requests, once each in first-seen order. */
const distinctEntityTypes = (requests: TOnboardAsModeRequest[]): TSelectableEntityType[] => [
  ...new Set(
    requests
      .map(entityRequestType)
      .filter((type): type is TSelectableEntityType => type !== undefined),
  ),
];

/**
 * The entity types of this client's granted entity requests (closed by a grant), once each in
 * first-seen order. A discarded request (closed, no permission), an Individual request and another
 * client's request are not among them.
 */
export function grantedEntityTypes(
  requests: TOnboardAsModeRequest[] | undefined,
  organisationId: string | undefined,
): TSelectableEntityType[] {
  return distinctEntityTypes(
    (requests ?? []).filter(
      (request) =>
        request.organisationId === organisationId && !isOpen(request) && !!request.permissionId,
    ),
  );
}

/**
 * The entity types this client has an open request for, once each. A granted or discarded request,
 * an Individual request and another client's request are not among them. An invite of one declared
 * type that is none of these is a late invite (#572 Q4), and one that declares a type the client
 * holds as a granted request is not sent Home either (#708 Q1): the choose route and activation
 * both ask this with these types.
 */
export function openClientEntityRequestTypes(
  requests: TOnboardAsModeRequest[] | undefined,
  organisationId: string | undefined,
): TSelectableEntityType[] {
  return distinctEntityTypes(
    (requests ?? []).filter(
      (request) => request.organisationId === organisationId && isOpen(request),
    ),
  );
}

/**
 * True once an entity request for this client was granted. A grant is not this invite's
 * confirmation and does not lock the choice on the confirm screen: only an open request does
 * (#688 Q1).
 */
export function hasGrantedEntityRequest(
  requests: TOnboardAsModeRequest[] | undefined,
  organisationId: string | undefined,
): boolean {
  return grantedEntityTypes(requests, organisationId).length > 0;
}

/**
 * The open Individual or entity request with this id, or undefined for a request that is closed,
 * Legacy, of an unknown type, or not there.
 */
export function openAskFirstRequestById<T extends TOnboardAsModeRequest>(
  requests: T[] | undefined,
  requestId: string,
): T | undefined {
  return (requests ?? []).find(
    (request) => request.id === requestId && isOpen(request) && isAskFirstRequest(request),
  );
}

/**
 * The choice this client's open requests show: the open entity request's type, else the
 * individual when an Individual request is open, else undefined.
 */
export function requestsChoice(
  requests: TOnboardAsModeRequest[] | undefined,
  organisationId: string | undefined,
): TEntityChoice | undefined {
  const open = openAskFirstRequestsFor(requests, organisationId);
  const entity = open.map(entityRequestType).find((type) => type !== undefined);
  if (entity) {
    return entity;
  }
  return open.some((request) => request.requestType === 'Individual') ? 'individual' : undefined;
}

/** A choice carried in the URL, or undefined for anything that is not one. */
export function parseEntityChoice(value: unknown): TEntityChoice | undefined {
  if (value === 'individual' || isEntityType(value)) {
    return value;
  }
  return undefined;
}
